import { Divider, Flex } from '@byecode/ui'
import type { CustomViewLayout, DataSourceAbstract, Field, SchemaProtocol, ViewType } from '@lighthouse/core'
import { CUSTOM_VIEW_LAYOUT } from '@lighthouse/core'
import { getViewColumns, Group4ByecodeUi } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { EventList } from '@/components/EventList'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import { ButtonSettingList } from '../../Button/ButtonSettingList'
import { QuickFilterSetting } from '../../Common/QuickFilterSetting'
import { AllowFieldSetting, AllowImportSetting, SwitchItem } from '../../Common/UserOperate'
import { AllowTreeSortSetting } from '../../Common/UserOperate/AllowTreeSortSetting'
import { AllowViewSortSetting } from '../../Common/UserOperate/AllowViewSortSetting'

type Configure = {
    canImport?: boolean
    canExport?: boolean
    canPrint?: boolean
    canSearch?: boolean
    canFilter?: boolean
    canSort?: boolean
    canGroup?: boolean
    canPaginate?: boolean
    canDragTreeSort?: boolean
    canDragViewSort?: boolean
}

const CONFIGURE: Record<CustomViewLayout, Configure> = {
    [CUSTOM_VIEW_LAYOUT.grid]: {
        canImport: true,
        canExport: true,
        canPrint: true,
        canSearch: true,
        canFilter: true,
        canSort: true,
        canGroup: true,
        canPaginate: true,
        canDragViewSort: true
    },
    [CUSTOM_VIEW_LAYOUT.carousel]: {
        canImport: true,
        canExport: true,
        canPrint: true,
        canSearch: true,
        canFilter: true,
        canSort: true
    },
    [CUSTOM_VIEW_LAYOUT.waterfall]: {
        canImport: true,
        canExport: true,
        canPrint: true,
        canSearch: true,
        canFilter: true,
        canSort: true,
        canGroup: true,
        canPaginate: true
    },
    [CUSTOM_VIEW_LAYOUT.weekCalendar]: {
        canImport: true,
        canExport: true,
        canPrint: true,
        canSearch: true,
        canSort: true,
        canGroup: true
    },
    [CUSTOM_VIEW_LAYOUT.monthCalendar]: {
        canImport: true,
        canExport: true,
        canPrint: true,
        canSearch: true
    },
    [CUSTOM_VIEW_LAYOUT.comment]: {},
    [CUSTOM_VIEW_LAYOUT.tree]: {
        canDragTreeSort: true
    },
    [CUSTOM_VIEW_LAYOUT.multiDimensional]: {}
}

interface OperateProps {
    id: string
    dataSource: DataSourceAbstract
}

export const Operate: React.FC<OperateProps> = ({ id, dataSource }) => {
    const {
        id: dsId,
        viewOptions: { tableProps },
        schema,
        type
    } = dataSource
    const { watch, setValue, control } = useFormContext()
    const [
        layout,
        direction,
        wrap,
        viewFieldSettings,
        canSort,
        userSortSetting,
        canImport,
        userImportSetting,
        triggerWorkflow,
        canDragTreeSort,
        canDragViewSort
    ] = watch([
        'breakPoint.viewLayout.layout',
        'breakPoint.viewLayout.direction',
        'breakPoint.viewLayout.wrap',
        'viewFieldSettings',
        'canSort',
        'userSortSetting',
        'canImport',
        'userImportSetting',
        'triggerWorkflow',
        'canDragTreeSort',
        'canDragViewSort'
    ])
    const viewLayout = layout as CustomViewLayout
    const isWrappedHorizontalGrid = viewLayout === CUSTOM_VIEW_LAYOUT.grid && direction === 'horizontal' && wrap === true
    const viewBaseInfo = useMemo(() => ({ viewType: 'custom', viewDsId: dsId } as const), [dsId])
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const columns = useMemo(
        () =>
            getViewColumns({
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [schema, tableProps, viewFieldSettings]
    )

    // 合并数据源与视图上的字段
    const mergedSchema = useMemo(() => {
        return columns.reduce<SchemaProtocol['schema']>((acc, item) => {
            acc[item.fieldId] = { ...item, id: item.fieldId, name: item.title } as Field
            return acc
        }, {})
    }, [columns])

    return (
        <>
            <Group4ByecodeUi
                label={
                    <PopoverQuestion
                        type="toolOperation"
                        arrowOffset={20}
                        renderTarget={(node, ref) => (
                            <Flex gap={4} alignItems="center" ref={ref}>
                                工具栏操作{node}
                            </Flex>
                        )}
                    />
                }
            >
                <div style={{ padding: '0 8px' }}>
                    <ButtonSettingList prefixName="toolbar" hideConfigs={{ fillWay: true }} />
                </div>
            </Group4ByecodeUi>

            <Divider color="var(--color-gray-200)" />
            <Controller
                control={control}
                name="eventList"
                render={({ field }) => <EventList blockType="view" view={viewBaseInfo} value={field.value} onChange={field.onChange} />}
            />

            {Object.keys(CONFIGURE[viewLayout]).length > 0 && (
                <>
                    <Divider color="var(--color-gray-200)" />

                    <Group4ByecodeUi label="其它操作">
                        {CONFIGURE[viewLayout].canImport && (
                            <AllowImportSetting
                                type="UploadSimple"
                                title="批量导入"
                                columns={columns}
                                value={{ canImport, userImportSetting, triggerWorkflow }}
                                dataSource={dataSource}
                                dataSourceList={dataSourceList}
                                onChange={val => {
                                    const { canImport, userImportSetting, triggerWorkflow } = val
                                    setValue('canImport', canImport)
                                    setValue('userImportSetting', userImportSetting)
                                    setValue('triggerWorkflow', triggerWorkflow)
                                }}
                            />
                        )}

                        {/* {CONFIGURE[viewLayout].canPrint && (
                            <AllowPrintSetting
                                type="Print"
                                title="打印"
                                dsId={dsId}
                                value={{ canPrint }}
                                onChange={val => {
                                    const { canPrint } = val
                                    setValue('canPrint', canPrint)
                                }}
                            />
                        )}
                        {CONFIGURE[viewLayout].canExport && (
                            <AllowExportSetting
                                type="ImportExcel"
                                title="导出"
                                dsId={dsId}
                                columns={columns}
                                dataSource={dataSource}
                                dataSourceList={dataSourceList}
                                value={{ canExport }}
                                onChange={val => {
                                    const { canExport } = val
                                    setValue('canExport', canExport)
                                }}
                            />
                        )} */}

                        {CONFIGURE[viewLayout].canSearch && (
                            <Controller
                                control={control}
                                name="canSearch"
                                render={({ field }) => (
                                    <SwitchItem icon="SearchLine" label="搜索数据" value={field.value} onChange={field.onChange} />
                                )}
                            />
                        )}

                        {CONFIGURE[viewLayout].canFilter && (
                            <QuickFilterSetting schema={mergedSchema} dataSourceList={dataSourceList} columns={columns} />
                        )}

                        {CONFIGURE[viewLayout].canSort && (
                            <AllowFieldSetting
                                type="ArrowsDownUp"
                                title="排序"
                                columns={columns}
                                value={{ canSort, userSortSetting }}
                                onChange={val => {
                                    const { canSort, userSortSetting } = val
                                    setValue('canSort', canSort)
                                    setValue('userSortSetting', userSortSetting)
                                }}
                            />
                        )}

                        {CONFIGURE[viewLayout].canGroup && (
                            <Controller
                                control={control}
                                name="canGroup"
                                render={({ field }) => (
                                    <SwitchItem icon="GroupBy" label="设置分组" value={field.value} onChange={field.onChange} />
                                )}
                            />
                        )}

                        {CONFIGURE[viewLayout].canPaginate && !isWrappedHorizontalGrid && (
                            <Controller
                                control={control}
                                name="canPaginate"
                                render={({ field }) => (
                                    <SwitchItem icon="PageBreakBar" label="分页栏" value={field.value} onChange={field.onChange} />
                                )}
                            />
                        )}

                        {CONFIGURE[viewLayout].canDragViewSort && (
                            <AllowViewSortSetting
                                type="ClickButton"
                                title="拖拽排序"
                                dataSource={dataSource}
                                value={{ canDragViewSort }}
                                onChange={val => {
                                    const { canDragViewSort } = val
                                    setValue('canDragViewSort', canDragViewSort)
                                }}
                            />
                        )}

                        {CONFIGURE[viewLayout].canDragTreeSort && (
                            <AllowTreeSortSetting
                                type="ClickButton"
                                title="拖拽改变层级和排序"
                                dataSource={dataSource}
                                value={{ canDragTreeSort }}
                                onChange={val => {
                                    const { canDragTreeSort } = val
                                    setValue('canDragTreeSort', canDragTreeSort)
                                }}
                            />
                            // <Controller
                            //     control={control}
                            //     name="canDragTreeSort"
                            //     render={({ field }) => (

                            //         <SwitchItem
                            //             icon="ClickButton"
                            //             label={
                            //                 <Flex gap={4} alignItems="center">
                            //                     拖拽改变层级和排序
                            //                     <Tooltip
                            //                         title="启用后可通过拖拽改变树节点的层级关系和排序。注意：将会禁用「排序」配置"
                            //                         width={200}
                            //                     >
                            //                         <IconFont type="Question" fill="var(--color-gray-400)" size={16} />
                            //                     </Tooltip>
                            //                 </Flex>
                            //             }
                            //             value={field.value}
                            //             onChange={field.onChange}
                            //         />
                            //     )}
                            // />
                        )}
                    </Group4ByecodeUi>
                </>
            )}
        </>
    )
}
